// src/contexts/PetContext.jsx
// Pet-based dashboard: fetches user's pets, defaults to first (alphabetically), provides selectedPet for API calls.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

const PetContext = createContext();

export function PetProvider({ children }) {
  const { getToken, isLoaded: authLoaded } = useAuth();
  const { isLoaded: userLoaded } = useUser();

  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPets() {
      if (!authLoaded || !userLoaded) return;

      try {
        setIsLoading(true);
        const token = await getToken();
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';
        const response = await fetch(`${API_BASE_URL}/pets`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch pets: ${response.status}`);
        }

        const data = await response.json();
        const list = data.pets || data.accounts || [];
        setPets(list);
        setSelectedPet(list.length > 0 ? list[0] : null);
      } catch (err) {
        console.error('Error fetching pets:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchPets();
  }, [getToken, authLoaded, userLoaded]);

  const switchPet = (petId) => {
    const pet = pets.find((p) => p.id === petId);
    if (pet) {
      setSelectedPet(pet);
    }
  };

  const value = {
    pets,
    selectedPet,
    switchPet,
    isLoading,
    error,
  };

  return (
    <PetContext.Provider value={value}>
      {children}
    </PetContext.Provider>
  );
}

export function usePet() {
  const context = useContext(PetContext);
  if (!context) {
    throw new Error('usePet must be used within a PetProvider');
  }
  return context;
}
